import {
	POLYGON_RPC_URL,
	POLYMARKET_EXCHANGE_V2,
	POLYMARKET_NEG_RISK_EXCHANGE_V2,
} from '@/constants/polymarket'
import {
	COLLATERAL_ONRAMP_CONTRACT_ADDRESS,
	CTF_COLLATERAL_ADAPTER_ADDRESS,
	CTF_CONTRACT_ADDRESS,
	NEG_RISK_ADAPTER_ADDRESS,
	NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS,
	PUSD_CONTRACT_ADDRESS,
	USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens'
import { OperationType, type SafeTransaction } from '@polymarket/builder-relayer-client'
import { http, createPublicClient, encodeFunctionData, erc20Abi } from 'viem'
import { polygon } from 'viem/chains'

const MAX_UINT256 = '115792089237316195423570985008687907853269984665640564039457584007913129639935'

const erc1155Abi = [
	{
		inputs: [
			{ name: 'operator', type: 'address' },
			{ name: 'approved', type: 'bool' },
		],
		name: 'setApprovalForAll',
		outputs: [],
		stateMutability: 'nonpayable',
		type: 'function',
	},
	{
		inputs: [
			{ name: 'account', type: 'address' },
			{ name: 'operator', type: 'address' },
		],
		name: 'isApprovedForAll',
		outputs: [{ name: '', type: 'bool' }],
		stateMutability: 'view',
		type: 'function',
	},
] as const

const publicClient = createPublicClient({
	chain: polygon,
	transport: http(POLYGON_RPC_URL),
})

// V2 collateral (pUSD) spenders:
// - V2 Exchange + V2 Neg Risk Exchange — settle trades, pull pUSD on BUY
// - CtfCollateralAdapter — V2 wrapper for standard split/merge/redeem;
//   pulls pUSD and bridges into bare CTF
// - NegRiskCtfCollateralAdapter — V2 wrapper for neg-risk split/merge/
//   redeem; pulls pUSD and bridges into bare NegRiskAdapter
// - NegRiskAdapter — long-lived contract reused by V2; handles split/merge
//   math for neg-risk multi-outcome markets. Empirically required by CLOB V2
//   (server returns 400 "allowance is not enough" on neg-risk trades without it).
//   Not listed in the V2 contracts page but unchanged from V1.
const PUSD_SPENDERS = [
	{ address: POLYMARKET_EXCHANGE_V2, name: 'V2 Exchange' },
	{ address: POLYMARKET_NEG_RISK_EXCHANGE_V2, name: 'V2 Neg Risk Exchange' },
	{ address: CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'CTF Collateral Adapter' },
	{ address: NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'Neg Risk CTF Collateral Adapter' },
	{ address: NEG_RISK_ADAPTER_ADDRESS, name: 'Neg Risk Adapter' },
] as const

// USDC.e only needs to be approved for the Collateral Onramp so wrap() can
// pull USDC.e and mint pUSD. No other V2 contract takes USDC.e directly.
const USDC_E_SPENDERS = [
	{ address: COLLATERAL_ONRAMP_CONTRACT_ADDRESS, name: 'Collateral Onramp' },
] as const

// Outcome tokens (ERC-1155 CTF) approved for V2 Exchange contracts + adapter +
// the long-lived NegRiskAdapter (used during split/merge of neg-risk positions).
const OUTCOME_TOKEN_SPENDERS = [
	{ address: POLYMARKET_EXCHANGE_V2, name: 'V2 Exchange' },
	{ address: POLYMARKET_NEG_RISK_EXCHANGE_V2, name: 'V2 Neg Risk Exchange' },
	{ address: CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'CTF Collateral Adapter' },
	{ address: NEG_RISK_ADAPTER_ADDRESS, name: 'Neg Risk Adapter' },
] as const

const checkErc20ApprovalForSpender = async (
	tokenAddress: string,
	safeAddress: string,
	spender: string
): Promise<boolean> => {
	try {
		const allowance = await publicClient.readContract({
			address: tokenAddress as `0x${string}`,
			abi: erc20Abi,
			functionName: 'allowance',
			args: [safeAddress as `0x${string}`, spender as `0x${string}`],
		})

		const threshold = BigInt('1000000000000')
		return allowance >= threshold
	} catch (error) {
		console.warn(`Failed to check ${tokenAddress} approval for ${spender}:`, error)
		return false
	}
}

const checkERC1155ApprovalForSpender = async (
	safeAddress: string,
	spender: string
): Promise<boolean> => {
	try {
		const isApproved = await publicClient.readContract({
			address: CTF_CONTRACT_ADDRESS as `0x${string}`,
			abi: erc1155Abi,
			functionName: 'isApprovedForAll',
			args: [safeAddress as `0x${string}`, spender as `0x${string}`],
		})

		return isApproved
	} catch (error) {
		console.warn(`Failed to check ERC1155 approval for ${spender}:`, error)
		return false
	}
}

export const checkAllApprovals = async (
	safeAddress: string
): Promise<{
	allApproved: boolean
	pusdApprovals: Record<string, boolean>
	usdcEApprovals: Record<string, boolean>
	outcomeTokenApprovals: Record<string, boolean>
}> => {
	const pusdApprovals: Record<string, boolean> = {}
	const usdcEApprovals: Record<string, boolean> = {}
	const outcomeTokenApprovals: Record<string, boolean> = {}

	await Promise.all([
		...PUSD_SPENDERS.map(async ({ address, name }) => {
			pusdApprovals[name] = await checkErc20ApprovalForSpender(
				PUSD_CONTRACT_ADDRESS,
				safeAddress,
				address
			)
		}),
		...USDC_E_SPENDERS.map(async ({ address, name }) => {
			usdcEApprovals[name] = await checkErc20ApprovalForSpender(
				USDC_E_CONTRACT_ADDRESS,
				safeAddress,
				address
			)
		}),
		...OUTCOME_TOKEN_SPENDERS.map(async ({ address, name }) => {
			outcomeTokenApprovals[name] = await checkERC1155ApprovalForSpender(safeAddress, address)
		}),
	])

	const allApproved =
		Object.values(pusdApprovals).every(approved => approved) &&
		Object.values(usdcEApprovals).every(approved => approved) &&
		Object.values(outcomeTokenApprovals).every(approved => approved)

	return {
		allApproved,
		pusdApprovals,
		usdcEApprovals,
		outcomeTokenApprovals,
	}
}

// Click-time defensive check: if the Safe doesn't already have a sufficient
// pUSD allowance to `spender`, returns an approve(spender, MAX) SafeTransaction
// that callers can prepend to their batch. Returns null if approval is already
// in place. Used by split/merge/redeem hooks to cover Safes that were
// initialized before a new spender was added to PUSD_SPENDERS — the session
// init flow re-runs when a new spender appears, but a user can click the
// action before that resync completes.
export const createPusdApprovalTxIfNeeded = async (
	safeAddress: string,
	spender: string
): Promise<SafeTransaction | null> => {
	const hasAllowance = await checkErc20ApprovalForSpender(
		PUSD_CONTRACT_ADDRESS,
		safeAddress,
		spender
	)
	if (hasAllowance) return null
	return {
		to: PUSD_CONTRACT_ADDRESS,
		operation: OperationType.Call,
		data: encodeFunctionData({
			abi: erc20Abi,
			functionName: 'approve',
			args: [spender as `0x${string}`, BigInt(MAX_UINT256)],
		}),
		value: '0',
	}
}

export const createAllApprovalTxs = (): SafeTransaction[] => {
	const safeTxns: SafeTransaction[] = []

	for (const { address } of PUSD_SPENDERS) {
		safeTxns.push({
			to: PUSD_CONTRACT_ADDRESS,
			operation: OperationType.Call,
			data: encodeFunctionData({
				abi: erc20Abi,
				functionName: 'approve',
				args: [address as `0x${string}`, BigInt(MAX_UINT256)],
			}),
			value: '0',
		})
	}

	for (const { address } of USDC_E_SPENDERS) {
		safeTxns.push({
			to: USDC_E_CONTRACT_ADDRESS,
			operation: OperationType.Call,
			data: encodeFunctionData({
				abi: erc20Abi,
				functionName: 'approve',
				args: [address as `0x${string}`, BigInt(MAX_UINT256)],
			}),
			value: '0',
		})
	}

	for (const { address } of OUTCOME_TOKEN_SPENDERS) {
		safeTxns.push({
			to: CTF_CONTRACT_ADDRESS,
			operation: OperationType.Call,
			data: encodeFunctionData({
				abi: erc1155Abi,
				functionName: 'setApprovalForAll',
				args: [address as `0x${string}`, true],
			}),
			value: '0',
		})
	}

	return safeTxns
}
